import { ChevronDown, ChevronUp, Info, MoreHorizontal } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Progress } from "../../components/ui/progress"
import truckImg  from "../../assets/truck.png"
import useSWR from "swr"
import { fetcher } from "../../util/axios"
import { Fragment, useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import dayjs from "dayjs";
import { Select, SelectProps } from "antd";


const PERIOD: SelectProps['options'] = [
  {
    label: 'Today',
    value: 'd',
  },
  {
    label: 'Current Week',
    value: '7d',
  },
  {
    label: 'Last Week',
    value: '1w',
  },
  {
    label: 'Last Months',
    value: '1m',
  },
  {
    label: 'Last 3 Months',
    value: '3m',
  },
  {
    label: 'Last 6 Months',
    value: '6m',
  },
  {
    label: 'Last year',
    value: '1yr',
  },
];

export function Dashboard() {
  const [activePeriod, setActivePeriod] = useState('3m'); // 3m, 6m or 1yr
  const { data, isLoading, } = useSWR({url: `dashboard/fleet-owner/?period=${activePeriod}`}, fetcher);

  const rentalsList = useMemo(() => {
    return data?.data?.rentalsList
  }, [data]);

  const vehicleUsageOverview = useMemo(() => {
    return data?.data?.vehicleUsageOverview
  }, [data]);

  const driverTrips = useMemo(() => {
    return data?.data?.driverTrips
  }, [data]);

  const complianceStats = useMemo(() => {
    return data?.data?.complianceStats
  }, [data]);

  const totalEarningsCard = useMemo(() => {
    return data?.data?.totalEarningsCard
  }, [data]);

  const pendingAssignments = useMemo(() => {
    return data?.data?.pendingAssignments
  }, [data]);

  const handleChange = (value: string) => {
    setActivePeriod(value);
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <Select
          size="middle"
          defaultValue={activePeriod}
          onChange={handleChange}
          style={{ width: 200 }}
          options={PERIOD}
          className="!ml-auto"
        />
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />        
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <UpdateCard />
        <PendingRentalsAndEarningsSummery
          {...totalEarningsCard}
          pendingAssignments={pendingAssignments}
        />
        <DriverTripsCard {...driverTrips} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <RentalRequestsCard rentalRequests={rentalsList} />
        <EarningsChartCard {...vehicleUsageOverview} />
        <ComplianceCard {...complianceStats} />
      </div>
    </div>
  )
}

function UpdateCard() {
  return (
    <Card className="bg-blue-600 text-white">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <div className="bg-blue-500 rounded-full p-1">
            <Info className="h-4 w-4" />
          </div>
          <CardTitle className="text-sm font-medium">Update</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-blue-200 mb-2">Feb 25th 2025</div>
        <div className="text-lg font-medium mb-2">Important Update - 3 trips ongoing, May require updating</div>
        <div className="text-xs text-blue-200">See more &gt;</div>
      </CardContent>
    </Card>
  )
}

interface PendingRentalsAndEarningsSummeryProps {
  amountCompact: string;
  amount: number;
  fromLastMonthChangePercent: number;
  pendingAssignments: number;
}

function PendingRentalsAndEarningsSummery({pendingAssignments, amount, amountCompact, fromLastMonthChangePercent}: PendingRentalsAndEarningsSummeryProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <h4 className="flex-1 mb-4 text-sm font-semibold">Pending Rentals</h4>
        <h4 className="flex-1 mb-4 text-sm font-semibold">Total Earnings</h4>
      </CardHeader>
      <CardContent className="h-full flex flex-col">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="text-3xl font-bold mb-4">{pendingAssignments}</div>
            <div className="text-xs text-muted-foreground mb-4">2 Categories Requested</div>          
          </div>
          <div className="flex-1">
            <div className="flex items-baseline">
              <span className="text-muted-foreground mr-1">N</span>
              <div className="text-3xl font-bold text-amber-500 mb-4">
                {amountCompact}
              </div>
            </div>
            <div className={twMerge(
              "flex items-center gap-1 text-xs mt-4",
              fromLastMonthChangePercent > 0 ? 'text-green-600': 'text-red-600')}>
                {fromLastMonthChangePercent > 0 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                <span>{fromLastMonthChangePercent}% from last month</span>
            </div>         
          </div>
        </div>
        <div className="mt-auto flex gap-2">
          <Button size="sm" className="flex-1">
            Assign
          </Button>
          <Button size="sm" variant="outline" className="flex-1">
            Auto-assign
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function TotalEarningsCard() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline">
          <span className="text-muted-foreground mr-1">$</span>
          <div className="text-3xl font-bold text-amber-500">27.450M</div>
        </div>
        <div className="flex items-center gap-1 text-xs text-green-600 mt-4">
          <ChevronUp className="h-3 w-3" />
          <span>26% from last month</span>
        </div>
      </CardContent>
    </Card>
  )
}

interface RentalRequestsCardProps {
  rentalRequests: {
    id: string;
    status: string;
    vehicleType: string;
    requestDate: string;
    tripsRequired: number;
  }[];
}


function RentalRequestsCard({ rentalRequests=[] }: RentalRequestsCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">Rental Requests</CardTitle>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[400px] overflow-auto py-4">
          {rentalRequests.map((request) => (
            <div key={request.id} className="flex items-center gap-3 p-3 border-b border-gray-300 last:border-0">
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                <img src={truckImg } width={40} height={40} alt="Dump Truck" />
              </div>
              <div className="flex-1 flex flex-col">
                <span className="font-semibold text-sm">{request.vehicleType}</span>
                <span className="text-xs text-muted-foreground">{dayjs(request.requestDate).format('MMM D YYYY')}</span>
              </div>
              <div className="flex flex-col text-right">
                <span className={`text-sm font-semibold ${request.status === "PENDING" ? "text-blue-600" : "text-gray-500"}`}>
                  {request.status}
                </span>
                <span className="text-xs text-muted-foreground">{request.tripsRequired} Trips</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

    </Card>
  )
}

interface EarningsChartCardProps {
  labels: string[];
  series: {
    name: 'Dump Truck' | 'Flatbed';
    data: number[]; 
  }[]
}

function EarningsChartCard({labels=[], series=[]}: EarningsChartCardProps) {
  const colorMapping = {
    'Dump Truck': 'bg-orange-500',
    'Flatbed': 'bg-blue-500'
  }

  function valuesToPercentHeights(values: number[]) {
    if (!Array.isArray(values) || values.length === 0) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);

    // If all values equal, return 100% for each (you can change to 0 if desired)
    if (max === min) return values.map(_ => 100);

    return values.map(v => {
      const fraction = (v - min) / (max - min); // 0..1
      return fraction * 100; // percentage 0..100
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex flex-wrap items-center gap-2">
          {series.map((row) => (<Fragment key={row.name}>
            <div className={twMerge("w-3 h-3 rounded", colorMapping[row.name])} />
            <span className="text-xs">{row.name}</span>
          </Fragment>))}
        </div>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="h-[300px] flex items-end justify-between gap-2">
          {labels.map((month, i) => (
            <div key={month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex justify-center gap-1 h-[220px] items-end">
                {series.map(row => (
                  <div
                    key={row.name}
                    className="w-4 bg-orange-500 rounded-sm"
                    style={{ height: `${valuesToPercentHeights(row.data)[i]}%` }}
                  />
                ))}
              </div>
              <div className="text-xs text-muted-foreground mt-2">{month}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

type Document = {
  daysToExpiry: number;
  expiryDate: string;
  type: string;
}
interface ComplianceCardProps {
  compliancePercentage: number;
  upcomingRenewals: {
    vehicleId: string;
    regPlate: string;
    documents: Document[];
  }[]
}

function ComplianceCard({ compliancePercentage, upcomingRenewals=[] }: ComplianceCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">Compliance Overview</CardTitle>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-4xl font-bold text-blue-600 mb-1">{compliancePercentage}%</div>
        <div className="text-sm font-medium mb-1">Overall Compliance</div>
        <div className="text-xs text-muted-foreground mb-4">
          Improved compliance means you never loose track of a document renewal
        </div>

        <Button className="w-full bg-black text-white hover:bg-gray-800">Upload documents</Button>

        <div className="mt-4">
          <div className="text-sm font-medium mb-2">Upcoming Renewals</div>

          {upcomingRenewals.map((renewal) => (
            <div key={renewal.vehicleId} className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                <img src={truckImg } width={32} height={32} alt="Vehicle" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm">{renewal.regPlate}</div>
                <div className="text-xs text-muted-foreground">
                  expires <span className="text-blue-600">
                    JAN 20th 2025
                    {dayjs(renewal.documents[0].daysToExpiry).format('MMM D YYYY')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

interface DriverTripsCardProps {
  labels: string[];
  series: number[];
}

function DriverTripsCard({labels=[], series=[]}: DriverTripsCardProps) {
  const maxTrips = Math.max(...series.map((n) => n))

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">Driver Trips</CardTitle>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {labels.map((driver, idx) => (
            <div key={idx} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>{driver}</span>
                {/* <span>{driver.trips}</span> */}
              </div>
              <Progress
                value={(series[idx] / maxTrips) * 100}
                className="h-2 bg-blue-100"
                // indicatorClassName="bg-blue-600"
              />
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-4">
          <div className="text-xs text-muted-foreground">0</div>
          <div className="text-xs text-muted-foreground">5</div>
          <div className="text-xs text-muted-foreground">10</div>
          <div className="text-xs text-muted-foreground">15</div>
          <div className="text-xs text-muted-foreground">20</div>
          <div className="text-xs text-muted-foreground">25</div>
        </div>
      </CardContent>
    </Card>
  )
}
